// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Search, ChevronLeft, ChevronRight, X,
  AlertCircle, Check, Ban, RotateCcw, Loader2, KeyRound, Pencil
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

function StatusBadge({ status }) {
  const config = {
    ACTIVE: { bg: '#E8F5E9', text: '#2E7D32', label: 'Active' },
    SUSPENDED: { bg: '#FFEBEE', text: '#C62828', label: 'Suspended' },
    EXPIRED: { bg: '#FFF3E0', text: '#E65100', label: 'SIA Expired' }
  };
  const style = config[status] || config.ACTIVE;
  return (
    <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: style.bg, color: style.text }}>
      {style.label}
    </span>
  );
}

function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(5)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-48 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-6 w-16 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-8 w-24 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="p-12 text-center">
      <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="font-medium text-gray-600">No officers yet</p>
      <p className="text-sm text-gray-400 mt-1 mb-4">Add your first officer to get started</p>
      <button
        onClick={onAdd}
        className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
        style={{ backgroundColor: VCIN_COLORS.primary }}
      >
        Add Officer
      </button>
    </div>
  );
}

function OfficerFormPanel({ isOpen, onClose, sites, onSuccess, editingOfficer }) {
  const isEdit = !!editingOfficer;

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', mobile_number: '',
    sia_number: '', sia_expiry: '', assigned_sites: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingOfficer) {
      setFormData({
        first_name: editingOfficer.first_name || editingOfficer.full_name?.split(' ')[0] || '',
        last_name: editingOfficer.last_name || editingOfficer.full_name?.split(' ').slice(1).join(' ') || '',
        email: editingOfficer.email || '',
        mobile_number: editingOfficer.mobile_number || '',
        sia_number: editingOfficer.sia_number || '',
        sia_expiry: editingOfficer.sia_expiry
          ? new Date(editingOfficer.sia_expiry).toISOString().split('T')[0]
          : '',
        assigned_sites: editingOfficer.assigned_sites?.map(s => s.id || s) || []
      });
    } else {
      setFormData({
        first_name: '', last_name: '', email: '', mobile_number: '',
        sia_number: '', sia_expiry: '', assigned_sites: []
      });
    }
    setError(null);
    setErrors({});
  }, [editingOfficer, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!isEdit && !formData.email.trim()) newErrors.email = 'Email is required';
    else if (!isEdit && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.mobile_number.trim()) newErrors.mobile_number = 'Mobile number is required';
    if (!formData.sia_number.trim()) newErrors.sia_number = 'SIA number is required';
    if (!formData.sia_expiry) newErrors.sia_expiry = 'SIA expiry date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError(null);
    try {
      if (isEdit) {
        await api.put(`/officers/${editingOfficer.id}`, formData);
      } else {
        await api.post('/officers', formData);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} officer`);
    } finally {
      setLoading(false);
    }
  };

  const handleSiteToggle = (siteId) => {
    setFormData(prev => ({
      ...prev,
      assigned_sites: prev.assigned_sites.includes(siteId)
        ? prev.assigned_sites.filter(id => id !== siteId)
        : [...prev.assigned_sites, siteId]
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Officer' : 'Add New Officer'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.first_name ? 'border-red-300' : 'border-gray-300'}`}
              />
              {errors.first_name && <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.last_name ? 'border-red-300' : 'border-gray-300'}`}
              />
              {errors.last_name && <p className="mt-1 text-xs text-red-600">{errors.last_name}</p>}
            </div>
          </div>

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="officer@company.co.uk"
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
          )}

          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-400">Email cannot be changed</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
            <input
              type="tel"
              value={formData.mobile_number}
              onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.mobile_number ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="+44 7XXX XXXXXX"
            />
            {errors.mobile_number && <p className="mt-1 text-xs text-red-600">{errors.mobile_number}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SIA Licence # *</label>
              <input
                type="text"
                value={formData.sia_number}
                onChange={(e) => setFormData({ ...formData, sia_number: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.sia_number ? 'border-red-300' : 'border-gray-300'}`}
              />
              {errors.sia_number && <p className="mt-1 text-xs text-red-600">{errors.sia_number}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SIA Expiry *</label>
              <input
                type="date"
                value={formData.sia_expiry}
                onChange={(e) => setFormData({ ...formData, sia_expiry: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.sia_expiry ? 'border-red-300' : 'border-gray-300'}`}
              />
              {errors.sia_expiry && <p className="mt-1 text-xs text-red-600">{errors.sia_expiry}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Sites</label>
            <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto">
              {sites.length === 0 ? (
                <p className="text-sm text-gray-400">No sites available</p>
              ) : (
                sites.map(site => (
                  <label key={site.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 px-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.assigned_sites.includes(site.id)}
                      onChange={() => handleSiteToggle(site.id)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{site.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 pt-2">
            Officer data is processed under <a href="/privacy" className="underline text-blue-900">VCIN's Privacy Notice</a>.
          </p>

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-11 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              style={{ backgroundColor: VCIN_COLORS.primary }}
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" />{isEdit ? 'Saving...' : 'Creating...'}</>
              ) : (
                isEdit ? 'Save Changes' : 'Create Officer'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default function OfficersPage() {
  const [officers, setOfficers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showPanel, setShowPanel] = useState(false);
  const [editingOfficer, setEditingOfficer] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchOfficers = async (page = 1) => {
    try {
      setLoading(true);
      const [officersRes, sitesRes] = await Promise.all([
        api.get(`/officers?page=${page}&limit=10`),
        api.get('/sites')
      ]);
      setOfficers(officersRes.data.officers);
      setPagination(officersRes.data.pagination);
      setSites(sitesRes.data.sites || []);
    } catch (err) {
      setError(err.message || 'Failed to load officers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOfficers(); }, []);

  const handleStatusChange = async (officerId, newStatus) => {
    try {
      setActionLoading(officerId);
      await api.patch(`/officers/${officerId}/status`, { status: newStatus });
      await fetchOfficers(pagination.page);
    } catch (err) {
      alert(err.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (officerId, officerName) => {
    if (!window.confirm(`Send password reset email to ${officerName}?`)) return;
    try {
      setActionLoading(`reset-${officerId}`);
      const response = await api.post(`/officers/${officerId}/reset-password`);
      alert(`Password reset email sent to ${response.data.email}`);
    } catch (err) {
      alert(err.message || 'Failed to send password reset email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (officer) => {
    setEditingOfficer(officer);
    setShowPanel(true);
  };

  const handleClosePanel = () => {
    setShowPanel(false);
    setEditingOfficer(null);
  };

  const filteredOfficers = officers.filter(officer =>
    officer.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    officer.email?.toLowerCase().includes(search.toLowerCase()) ||
    officer.sia_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="officers-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Officers</h1>
        <button
          onClick={() => { setEditingOfficer(null); setShowPanel(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: VCIN_COLORS.primary }}
          data-testid="add-officer-btn"
        >
          <Plus className="w-4 h-4" />
          Add Officer
        </button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or SIA number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            data-testid="officer-search"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="officers-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SIA Number</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SIA Expiry</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            {loading ? (
              <TableSkeleton />
            ) : filteredOfficers.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="6">
                    <EmptyState onAdd={() => { setEditingOfficer(null); setShowPanel(true); }} />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {filteredOfficers.map((officer) => (
                  <tr key={officer.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <span className="font-medium text-gray-900">{officer.full_name}</span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {officer.assigned_sites && officer.assigned_sites.length > 0
                            ? officer.assigned_sites.map(site => site.name || site).join(', ')
                            : <span className="italic text-gray-400">Unassigned</span>
                          }
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {officer.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm text-gray-600">{officer.sia_number}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {officer.sia_expiry
                        ? new Date(officer.sia_expiry).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={officer.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(officer)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          data-testid={`edit-officer-${officer.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        {officer.status === 'ACTIVE' ? (
                          <button
                            onClick={() => handleStatusChange(officer.id, 'SUSPENDED')}
                            disabled={actionLoading === officer.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                          >
                            {actionLoading === officer.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                            Suspend
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(officer.id, 'ACTIVE')}
                            disabled={actionLoading === officer.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                          >
                            {actionLoading === officer.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                            Reactivate
                          </button>
                        )}
                        <button
                          onClick={() => handleResetPassword(officer.id, officer.full_name)}
                          disabled={actionLoading === `reset-${officer.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                          style={{ backgroundColor: `${VCIN_COLORS.primary}15`, color: VCIN_COLORS.primary }}
                        >
                          {actionLoading === `reset-${officer.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                          Reset Password
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 10 + 1} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} officers
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchOfficers(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => fetchOfficers(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <OfficerFormPanel
        isOpen={showPanel}
        onClose={handleClosePanel}
        sites={sites}
        onSuccess={() => fetchOfficers(pagination.page)}
        editingOfficer={editingOfficer}
      />
    </div>
  );
}
